import { StockSnapshotItem, StocksSnapshotPayload, AEVHistory } from '../types';
import { API_CONFIG } from '../constants';
import { decryptJsonResponse } from '../utils/cryptoUtils';

/**
 * Module-level memory cache for the lightweight NSE stocks snapshot.
 * Contains latest price, % change, volume, delivery %, and 30-day sparkline closes
 * for all traded stocks.
 */
let snapshotMap: Map<string, StockSnapshotItem> | null = null;
let inflightSnapshotPromise: Promise<Map<string, StockSnapshotItem>> | null = null;
let snapshotDate: string = '';

const snapshotUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STOCKS_SNAPSHOT_LATEST}`;

export const clearSnapshotCache = (): void => {
  snapshotMap = null;
  inflightSnapshotPromise = null;
  snapshotDate = '';
};

/**
 * Loads the latest stocks snapshot from backend / Azure Blob Storage.
 * Deduplicates concurrent requests so it is only fetched once per session.
 */
export const ensureSnapshotLoaded = async (options?: { forceRefresh?: boolean }): Promise<Map<string, StockSnapshotItem>> => {
  const forceRefresh = options?.forceRefresh ?? false;

  if (!forceRefresh && snapshotMap) {
    return snapshotMap;
  }

  if (!forceRefresh && inflightSnapshotPromise) {
    return inflightSnapshotPromise;
  }

  inflightSnapshotPromise = (async () => {
    try {
      const url = forceRefresh ? `${snapshotUrl}?v=${Date.now()}` : snapshotUrl;
      let response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          ...(forceRefresh ? { 'Cache-Control': 'no-cache, no-store', Pragma: 'no-cache' } : {}),
        },
        ...(forceRefresh ? { cache: 'no-store' } : {}),
      });

      if (!response.ok) {
        // Fallback to static public/data path if API proxy is 404/401 (e.g. dev server without restarted proxy or initial deploy)
        try {
          const fallbackRes = await fetch('/data/stocks-snapshot-latest.json', {
            headers: { Accept: 'application/json' },
          });
          if (fallbackRes.ok) {
            response = fallbackRes;
          } else {
            snapshotMap = new Map();
            return snapshotMap;
          }
        } catch {
          snapshotMap = new Map();
          return snapshotMap;
        }
      }

      const buffer = await response.arrayBuffer();
      let payload: StocksSnapshotPayload | null = null;

      try {
        const text = new TextDecoder('utf-8').decode(buffer);
        payload = JSON.parse(text);
      } catch {
        // Try decrypting if payload is encrypted binary
        try {
          payload = await decryptJsonResponse<StocksSnapshotPayload>(buffer);
        } catch {
          payload = null;
        }
      }

      const map = new Map<string, StockSnapshotItem>();
      if (payload && payload.stocks) {
        snapshotDate = payload.Date || '';
        for (const [sym, item] of Object.entries(payload.stocks)) {
          if (sym && item) {
            map.set(sym.toUpperCase(), item);
          }
        }
      }

      snapshotMap = map;
      return map;
    } catch (err) {
      console.warn('Failed to load stocks snapshot, will fallback to on-demand history:', err);
      snapshotMap = new Map();
      return snapshotMap;
    } finally {
      inflightSnapshotPromise = null;
    }
  })();

  return inflightSnapshotPromise;
};

export const getSnapshotItem = (symbol: string): StockSnapshotItem | undefined => {
  if (!snapshotMap) return undefined;
  return snapshotMap.get(symbol.toUpperCase());
};

export const hasSnapshotItem = (symbol: string): boolean => {
  if (!snapshotMap) return false;
  return snapshotMap.has(symbol.toUpperCase());
};

export const getSnapshotDate = (): string => snapshotDate;

/**
 * Returns the 30-day sparkline closes and accumulation marker flags
 * directly from the snapshot memory cache.
 */
export const getSnapshotSparkline = (
  symbol: string
): { data: number[]; markerFlags: boolean[] } | undefined => {
  const item = getSnapshotItem(symbol);
  if (!item || !Array.isArray(item.sp) || item.sp.length === 0) {
    return undefined;
  }

  return {
    data: item.sp,
    markerFlags: Array.isArray(item.af) ? item.af : [],
  };
};

/**
 * Converts a lightweight snapshot entry into a synthetic AEVHistory[] array.
 * Allows components expecting AEVHistory[] (like StockDetailCard) to display
 * full sparklines, prices, and changes immediately without downloading /{symbol}.json.
 */
export const convertSnapshotToSyntheticHistory = (symbol: string): AEVHistory[] | undefined => {
  const item = getSnapshotItem(symbol);
  if (!item || !Array.isArray(item.sp) || item.sp.length === 0) {
    return undefined;
  }

  const sparkline = item.sp;
  const flags = item.af || [];
  const synthetic: AEVHistory[] = [];

  const anchor = snapshotDate ? new Date(snapshotDate) : new Date();
  if (isNaN(anchor.getTime())) {
    anchor.setTime(Date.now());
  }

  // Pre-generate N weekdays backwards from anchor
  const dates: string[] = [];
  const cur = new Date(anchor);
  while (dates.length < sparkline.length) {
    const day = cur.getDay();
    if (day !== 0 && day !== 6) {
      dates.push(cur.toISOString().split('T')[0]);
    }
    cur.setDate(cur.getDate() - 1);
  }
  dates.reverse(); // Chronological: oldest to newest

  for (let i = 0; i < sparkline.length; i++) {
    const isLatest = i === sparkline.length - 1;
    const price = isLatest && item.p != null ? item.p : sparkline[i];
    synthetic.push({
      Date: dates[i] || (isLatest && snapshotDate ? snapshotDate : '2026-01-01'),
      ClosePrice: price,
      HighPrice: price,
      LowPrice: price,
      OpenPrice: price,
      PerChange: isLatest ? (item.chg ?? 0) : 0,
      Volume: isLatest ? (item.v ?? 0) : 0,
      AverageVol: isLatest && item.v ? item.v : 0,
      DeliveryPer: isLatest && item.d != null ? String(item.d) : '0',
      IsAccumulated: Boolean(flags[i]),
      Isdistributed: false,
    });
  }

  return synthetic;
};
